import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { getListingRegistrySummary } from "@/lib/registry/registry.functions";
import { RegistryBadge } from "@/components/registry/registry-badge";
import { registryPath } from "@/lib/registry/registry";
import { RegistryQRCode } from "@/components/registry/registry-qr-code";

function money(pence: number | null | undefined) {
  if (pence == null) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-md border border-border/50 bg-surface-2/40 p-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold">{value}</p>
    </div>
  );
}

/**
 * Buyer-facing Verified Card Registry panel on an auction detail page. Only
 * renders when the listing is actually linked to a permanent registry record.
 */
export function RegistrySection({ auctionId }: { auctionId: string }) {
  const fetchSummary = useServerFn(getListingRegistrySummary);
  const q = useQuery({
    queryKey: ["listing-registry", auctionId],
    queryFn: () => fetchSummary({ data: { listingId: auctionId } }),
    staleTime: 60_000,
  });

  const data = q.data;
  if (!data?.record) return null;
  const { record, market } = data;
  const path = registryPath(record.provider, record.certificate_number);

  return (
    <section className="rounded-lg border border-border/60 bg-surface-2/30 p-4" id="registry">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:justify-between">
        <h2 className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold uppercase tracking-wide">
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
          <span className="truncate">BidNova Verified Card Registry</span>
        </h2>
        <Link
          to={path}
          className="shrink-0 text-xs font-medium text-gold underline underline-offset-2"
        >
          View permanent record
        </Link>
      </div>

      <div className="mt-3 sm:float-right sm:ml-4">
        <RegistryQRCode
          path={path}
          size={112}
          label={record.card_name ?? record.certificate_number}
        />
      </div>

      <div className="mt-3">
        <RegistryBadge
          provider={record.provider}
          certNumber={record.certificate_number}
          status={record.status}
          verificationStatus={record.verification_status}
          officialUrl={record.official_source_url}
          lastVerifiedAt={record.last_verified_at}
        />
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Grader" value={record.provider.toUpperCase()} />
        <Stat label="Grade" value={record.grade ?? "—"} />
        <Stat
          label="Population (total)"
          value={record.population_total != null ? String(record.population_total) : "Not published"}
        />
        <Stat label="Last sold" value={money(market?.latestSale ?? null)} />
      </dl>

      {market && market.sampleSize > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Based on {market.sampleSize} recorded sale{market.sampleSize === 1 ? "" : "s"} on BidNova ·
          average {money(market.averageSale)} · range {money(market.lowestSale)}–
          {money(market.highestSale)}.
        </p>
      )}

      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
        Population figures come from the grading company where published; market figures come from
        BidNova activity only and are not an official valuation or a guarantee of future price.
      </p>

      {record.official_source_url && (
        <a
          href={record.official_source_url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-300 underline underline-offset-2"
        >
          Check the certificate on {record.provider.toUpperCase()}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      )}
    </section>
  );
}
